import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
  Renderer2,
  ViewChild,
  ViewChildren,
} from '@angular/core';

import { delay, Subject, Subscription } from 'rxjs';
import { Messages } from 'src/app/constants/messages';
import { ZoneListHierarchyModel, ZonePlanModel } from 'src/app/model';
import { DataService, ServicecallService } from 'src/app/services';
import { GeneralService } from '../../services';

@Component({
  selector: 'app-zone-list',
  templateUrl: './zone-list.component.html',
  styleUrls: ['./zone-list.component.scss'],
})
export class ZoneListComponent implements OnInit {
  @Input() list: ZoneListHierarchyModel[];

  @Input() more: boolean = false;

  @Output() planClick = new Subject<ZonePlanModel>();

  @Output() moreAction = new EventEmitter<any>();

  @ViewChild('zoneLevelList') zoneLevelListRef: ElementRef;

  @ViewChild('zoneList') zoneListRef: ElementRef;

  @ViewChildren('zoneItem') zoneRefGroup: QueryList<ElementRef>;

  // Index Management
  visibleIndex: number;

  // Depth
  depth = 0;
  headerSuffixList: number[];
  depthLimit: number;

  // Miscellaneous
  readonly MESSAGES = Messages;
  private _scrollToZoneFlag = new Subject<string>();

  // Scroll elements
  scrollElements: ElementRef[] = [];

  // Subscription
  private _scrollToZoneSubscription: Subscription;
  private _afterClosedSubscription: Subscription;

  constructor(
    public generalService: GeneralService,
    private _dataService: DataService,
    private _renderer: Renderer2,
    private _apiService: ServicecallService
  ) {
    this._setVisibleIndex();
  }

  ngOnInit(): void {
    // Scroll to zone
    this._scrollToZone();
    this.depthLimit =
      this._apiService.getConfiguration('tenantGenericConfig')?.[
        `depthLimit`
      ] ?? 1000;
  }

  ngAfterViewInit(): void {
    // Scroll elements
    this.scrollElements = [this.zoneLevelListRef, this.zoneListRef];
  }

  ngOnDestroy(): void {
    this._scrollToZoneSubscription?.unsubscribe();
    this._afterClosedSubscription?.unsubscribe();
  }

  /**
   * Determines whether navigate to plan click on
   */
  onNavigateToPlanClick(event: MouseEvent, zone: ZoneListHierarchyModel) {
    // Get the hierarchy for a specific zone
    const nameHierarchy = this.getZoneNameHierarchy(zone?._id, this.list) ?? [];
    event?.stopPropagation();
    // Set plan model
    this.planClick.next({
      _id: zone?._id,
      name: zone?.name,
      floorPlan: zone?.floorPlan,
      assetCount: zone?.assetCount,
      deviceCount: zone?.deviceCount,
      zoneBreadCrumb: nameHierarchy,
      geoFence: zone?.geoFence ?? null,
    });
  }

  /**
   * Determines whether zone click on
   * @param event
   * @param anchor
   * @param isSuperContent
   */
  onZoneClick(event: MouseEvent, anchor: string, isSuperContent: boolean) {
    const VISIBILITY_CLASS = '-open';
    // Stop event propagation
    event?.stopPropagation();
    // Get element reference
    const elementRef = <HTMLElement>(
      this.zoneRefGroup
        ?.toArray()
        ?.find((element) => element?.nativeElement?.id === anchor)
        ?.nativeElement
    );
    const elementClassList = elementRef?.classList;
    if (elementClassList?.contains(VISIBILITY_CLASS)) {
      this._renderer?.removeClass(elementRef, VISIBILITY_CLASS);
    } else {
      this._renderer?.addClass(elementRef, VISIBILITY_CLASS);
    }
    // Scroll to exact position
    if (!isSuperContent) {
      this._scrollToZoneFlag.next(anchor);
    }
  }

  /**
   * Sets interact state
   * @param parentState
   * @returns true if interact state
   */
  setInteractState(parentState: boolean): boolean {
    return parentState ? false : true;
  }

  /**
   * Gets child category
   * @param zone
   * @returns child category
   */
  getChildCategory(zone: ZoneListHierarchyModel): string {
    return zone?.children?.[0]?.nature ?? null;
  }

  /**
   * Scrolls to zone
   */
  private _scrollToZone() {
    this._scrollToZoneSubscription = this._scrollToZoneFlag
      .pipe(delay(100))
      .subscribe({
        next: (anchor: string) => {
          const selZoneRef: ElementRef = <ElementRef>(
            this.zoneRefGroup
              ?.toArray()
              ?.find((value) => value?.nativeElement?.id === anchor)
          );
          selZoneRef?.nativeElement?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest',
          });
        },
      });
  }

  /**
   * Sets visible index
   */
  private _setVisibleIndex() {
    this.visibleIndex = -1;
  }

  /**
   * Gets the full name hierarchy path of a zone as an array
   * @param zoneId The ID of the zone to get the hierarchy for
   * @param list The complete zone list hierarchy
   * @returns Array of names representing the hierarchy path
   */
  getZoneNameHierarchy(
    zoneId: string,
    list: ZoneListHierarchyModel[]
  ): string[] {
    const nameHierarchy: string[] = [];

    const findZonePathRecursive = (
      zones: ZoneListHierarchyModel[],
      targetId: string,
      currentPath: string[] = []
    ): boolean => {
      for (const zone of zones) {
        // Create a new path including this zone
        const path = [...currentPath, zone.name];

        // Check if this is the target zone
        if (zone._id === targetId) {
          // Found the zone, update the nameHierarchy and return true
          nameHierarchy.push(...path);
          return true;
        }

        // If this zone has children, recursively search them
        if (zone.children && zone.children.length > 0) {
          const foundInChildren = findZonePathRecursive(
            zone.children,
            targetId,
            path
          );
          if (foundInChildren) {
            return true;
          }
        }
      }

      // Zone not found in this branch
      return false;
    };

    // Start the recursive search
    findZonePathRecursive(list, zoneId);

    return nameHierarchy;
  }

  onMoreActionClick(action: string, zone: ZoneListHierarchyModel): void {
    this.moreAction.emit({
      action: action,
      zone: zone,
    });
  }

  addZone(): void {
    this.moreAction.emit({
      action: 'addProjectZone',
      zone: null,
    });
  }
}
